import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, TextField, Button, Alert, Grid,
    CircularProgress, Chip, Stepper, Step, StepLabel,
    RadioGroup, FormControlLabel, Radio, FormControl, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';
import { publicAPI, trainerAPI } from '../../services/api';

// ═══════════════════════════════════════════════════════════════
// Document requirements per statut from OFPPT reconduction rules
// ═══════════════════════════════════════════════════════════════

const STATUT_OPTIONS = [
    { value: 'retraite', label: 'Retraité(e)' },
    { value: 'actif_prive', label: 'Actif en secteur privé' },
    { value: 'actif_public', label: 'Actif en secteur public (Fonctionnaire)' },
    { value: 'actif_public_men', label: 'Actif en secteur public — Établissement MEN' },
];

const DOCS_RETRAITE = [
    {
        key: 'cin', label: 'Copie conforme de la CIN',
        info: 'CIN en cours de validité. Date de légalisation précisée.',
    },
    {
        key: 'diplome', label: 'Copie certifiée du diplôme',
        info: 'En fonction des modules pris en charge et validés par le CDC. Date de légalisation récente.',
    },
    {
        key: 'attestation_experience', label: 'Copie légalisée de l\'attestation d\'expérience',
        info: 'Portant la période de l\'expérience.',
    },
    {
        key: 'attestation_pension', label: 'Copie légalisée de l\'attestation de pension',
        info: 'Légalisation récente.',
    },
    {
        key: 'rib', label: 'Attestation de RIB',
        info: 'Portant le cachet de la banque. En cas du changement du RIB, fournir une demande légalisée de changement du compte bancaire.',
    },
    {
        key: 'cv', label: 'CV',
        info: 'Actualisé.',
    },
];

const DOCS_ACTIF_BASE = [
    {
        key: 'cin', label: 'Copie conforme de la CIN',
        info: 'CIN en cours de validité. Date de légalisation précisée.',
    },
    {
        key: 'diplome', label: 'Copie certifiée du diplôme',
        info: 'En fonction des modules pris en charge et validés par le CDC. Date de légalisation récente.',
    },
    {
        key: 'attestation_travail', label: 'Attestation récente de travail',
        info: 'Date récente d\'obtention. Précisant la date d\'embauche. Signée avec le nom et la qualité du signataire.',
    },
];

const DOCS_PRIVE_EXTRA = [
    {
        key: 'bulletin_paie', label: 'Bulletin de paie récent',
        info: 'Date récente d\'obtention. Cacheté. Avec une date de recrutement identique à celle qui figure sur l\'attestation de travail.',
    },
    {
        key: 'attestation_experience', label: 'Copies certifiées des attestations d\'expérience',
        info: 'Date de légalisation. Portant le numéro d\'immatriculation à la CNSS. Portant le nom et la qualité du signataire.',
    },
];

const DOCS_PUBLIC_EXTRA = [
    {
        key: 'autorisation', label: 'Autorisation récente (fonctionnaire)',
        info: 'Portant la date de sa signature. Précisant l\'année de formation 2025-2026.',
    },
    {
        key: 'attestation_experience', label: 'Copies certifiées des attestations d\'expérience',
        info: 'Date de légalisation. Portant le nom et la qualité du signataire.',
    },
];

const DOCS_MEN_EXTRA = [
    {
        key: 'situation_administrative', label: 'Dernière situation administrative (MEN)',
        info: 'Signée et cachetée avec le nom et la qualité du signataire.',
    },
    {
        key: 'autorisation', label: 'Autorisation récente (fonctionnaire)',
        info: 'Portant la date de sa signature. Précisant l\'année de formation 2025-2026.',
    },
    {
        key: 'attestation_experience', label: 'Copies certifiées des attestations d\'expérience',
        info: 'Date de légalisation. Portant le nom et la qualité du signataire.',
    },
];

const DOCS_ACTIF_COMMON_END = [
    {
        key: 'rib', label: 'Attestation de RIB',
        info: 'Cachetée. En cas du changement du RIB, fournir une demande légalisée de changement du compte bancaire.',
    },
    {
        key: 'cv', label: 'CV',
        info: 'Actualisé.',
    },
];

function getDocsForStatut(statut) {
    switch (statut) {
        case 'retraite':
            return DOCS_RETRAITE;
        case 'actif_prive':
            return [...DOCS_ACTIF_BASE,
                { key: 'attestation_travail_cnss', label: 'Attestation de travail (avec n° CNSS)', info: 'Avec un numéro de CNSS pour les employés du secteur privé.' },
                ...DOCS_PRIVE_EXTRA, ...DOCS_ACTIF_COMMON_END];
        case 'actif_public':
            return [...DOCS_ACTIF_BASE, ...DOCS_PUBLIC_EXTRA, ...DOCS_ACTIF_COMMON_END];
        case 'actif_public_men':
            return [...DOCS_ACTIF_BASE, ...DOCS_MEN_EXTRA, ...DOCS_ACTIF_COMMON_END];
        default:
            return [];
    }
}

// ═══════════════════════════════════════════════════════════════

const STEPS = ['Informations', 'Statut professionnel', 'Documents requis'];

export default function ApplyForm() {
    const { offerId } = useParams();
    const navigate = useNavigate();
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [activeStep, setActiveStep] = useState(0);

    const [form, setForm] = useState({
        cin: '', profession: '', diplomes: '', statut_professionnel: '',
        specialite_diplome: '', experience_professionnelle: '', metier: '',
    });

    // Files keyed by document key
    const [docFiles, setDocFiles] = useState({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadOffer(); }, [offerId]);

    const loadOffer = async () => {
        try {
            const { data } = await publicAPI.getJobOffer(offerId);
            setOffer(data);
        } catch (err) { setError('Offre non trouvée'); }
        finally { setLoading(false); }
    };

    const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const requiredDocs = getDocsForStatut(form.statut_professionnel);

    const canGoStep2 = form.cin && form.profession && form.diplomes && form.specialite_diplome && form.experience_professionnelle && form.metier;
    const canGoStep3 = form.statut_professionnel !== '';

    const handleSubmit = async () => {
        setError('');
        setSubmitting(true);

        try {
            // 1. Create candidature
            const { data: candidature } = await trainerAPI.applyCandidature({
                ...form,
                job_offer_id: Number(offerId),
            });

            // 2. Upload all document files
            for (const [docKey, file] of Object.entries(docFiles)) {
                if (file) {
                    const formData = new FormData();
                    formData.append('candidature_id', candidature.id);
                    formData.append('type', docKey);
                    formData.append('file', file);
                    await trainerAPI.uploadDocument(formData);
                }
            }

            setSuccess(true);
            toast.success('Candidature soumise avec succès !');
            setTimeout(() => navigate('/trainer/candidatures'), 2000);
        } catch (err) {
            if (err.response?.status === 409) {
                toast.warning('Vous avez déjà postulé pour cette offre.');
                setTimeout(() => navigate('/trainer/candidatures'), 2000);
            } else {
                setError(err.response?.data?.message || 'Erreur lors de la soumission');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

    if (success) {
        return (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" color="success.main" fontWeight={700} gutterBottom>
                    Candidature envoyée !
                </Typography>
                <Typography color="text.secondary">
                    Votre candidature a été soumise avec succès. Vous recevrez une réponse bientôt.
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/offres/${offerId}`)}
                sx={{ mb: 3, textTransform: 'none' }}>
                Retour à l'offre
            </Button>

            <Typography variant="h5" fontWeight={700} gutterBottom>
                Postuler : {offer?.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                {offer?.filieres?.map(f => (
                    <Chip key={f.id} label={f.nom} color="primary" size="small" />
                ))}
                <Chip label={`${offer?.total_hours}h`} variant="outlined" size="small" />
                <Chip label={offer?.required_levels?.join(', ')} variant="outlined" size="small" />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* ── Stepper ──────────────────────────────────── */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
                {STEPS.map((label) => (
                    <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
            </Stepper>

            {/* ═══ STEP 1: Informations ═══════════════════════ */}
            {activeStep === 0 && (
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        Informations du candidat
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="CIN" value={form.cin}
                                onChange={handleChange('cin')} required placeholder="Ex: AB123456" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Profession" value={form.profession}
                                onChange={handleChange('profession')} required />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Diplômes" multiline rows={2}
                                value={form.diplomes} onChange={handleChange('diplomes')} required
                                placeholder="Ex: Licence en informatique, Master en..." />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Spécialité du diplôme" value={form.specialite_diplome}
                                onChange={handleChange('specialite_diplome')} required placeholder="Ex: Développement Informatique" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Expérience professionnelle (Après l'obtention du diplôme)" value={form.experience_professionnelle}
                                onChange={handleChange('experience_professionnelle')} required placeholder="Ex: 5 ans" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Métier" value={form.metier}
                                onChange={handleChange('metier')} required placeholder="Ex: Ingénieur Logiciel" />
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button variant="contained" disabled={!canGoStep2}
                            onClick={() => setActiveStep(1)}
                            sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #1a5276, #2e86c1)' }}>
                            Suivant
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* ═══ STEP 2: Statut professionnel ═══════════════ */}
            {activeStep === 1 && (
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        Votre statut professionnel
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Les documents à fournir dépendent de votre statut. Veuillez choisir :
                    </Typography>
                    <FormControl required>
                        <RadioGroup value={form.statut_professionnel}
                            onChange={(e) => {
                                setForm({ ...form, statut_professionnel: e.target.value });
                                setDocFiles({});
                            }}>
                            {STATUT_OPTIONS.map(opt => (
                                <FormControlLabel key={opt.value} value={opt.value}
                                    control={<Radio />} label={opt.label}
                                    sx={{
                                        mb: 1, p: 1.5, borderRadius: 2, mx: 0,
                                        border: form.statut_professionnel === opt.value
                                            ? '2px solid #2e86c1' : '1px solid #e0e0e0',
                                        bgcolor: form.statut_professionnel === opt.value
                                            ? 'rgba(46,134,193,0.05)' : 'transparent',
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button onClick={() => setActiveStep(0)} sx={{ textTransform: 'none' }}>
                            Retour
                        </Button>
                        <Button variant="contained" disabled={!canGoStep3}
                            onClick={() => setActiveStep(2)}
                            sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #1a5276, #2e86c1)' }}>
                            Suivant — Voir les documents requis
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* ═══ STEP 3: Documents ═══════════════════════════ */}
            {activeStep === 2 && (
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        Documents requis — {STATUT_OPTIONS.find(s => s.value === form.statut_professionnel)?.label}
                    </Typography>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Veuillez fournir les documents ci-dessous. Formats acceptés : PDF, JPG, PNG.
                    </Alert>

                    {requiredDocs.map((doc, idx) => (
                        <Box key={doc.key + idx} sx={{
                            mb: 2, p: 2, borderRadius: 2,
                            border: docFiles[doc.key] ? '1px solid #27ae60' : '1px solid #e0e0e0',
                            bgcolor: docFiles[doc.key] ? 'rgba(39,174,96,0.03)' : '#fafbfc',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {idx + 1}. {doc.label}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
                                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.2 }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                                            {doc.info}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                    {docFiles[doc.key] && (
                                        <>
                                            <Chip label={docFiles[doc.key].name} size="small" color="success"
                                                variant="outlined" sx={{ maxWidth: 180 }}
                                                onDelete={() => {
                                                    const copy = { ...docFiles };
                                                    delete copy[doc.key];
                                                    setDocFiles(copy);
                                                }} />
                                        </>
                                    )}
                                    <Button variant="outlined" component="label" size="small"
                                        startIcon={docFiles[doc.key] ? <CheckCircleIcon /> : <AttachFileIcon />}
                                        color={docFiles[doc.key] ? 'success' : 'primary'}
                                        sx={{ textTransform: 'none', flexShrink: 0 }}>
                                        {docFiles[doc.key] ? 'Changer' : 'Choisir'}
                                        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => {
                                                if (e.target.files[0]) {
                                                    setDocFiles({ ...docFiles, [doc.key]: e.target.files[0] });
                                                }
                                            }} />
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    ))}

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={() => setActiveStep(1)} sx={{ textTransform: 'none' }}>
                            Retour
                        </Button>
                        <Button variant="contained" size="large" disabled={submitting}
                            onClick={handleSubmit}
                            sx={{
                                background: 'linear-gradient(135deg, #1a5276, #2e86c1)',
                                textTransform: 'none', borderRadius: 2, px: 5,
                            }}>
                            {submitting ? 'Envoi en cours...' : 'Soumettre ma candidature'}
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
